import React from "react";
import { SDLCMetadata, } from "@sscs/types";
import { Card, LongTextLabel, TooltipIcon } from "@sscs/ui";
import { TRANSFORM_EXPLOIT_FLAG } from "../../../constants";
import { getCvssSeverity } from "../../../utils";
import { VulnerabilityTooltip } from "../../common";
import RenderChildrenWrapper from "../../common/RenderChildrenWrapper";
import styles from "./vulnerabilities.module.scss";
const LIFECYCLE_TOOLTIP = (React.createElement(React.Fragment, null, "The lifecycle of a vulnerability begins upon discovery of a security exposure within a system. Once an exploit is identified, threat actors will often develop malicious code to target vulnerabilities in an automated manner. Finally, as vulnerabilities become widely known, they are actively exploited in the wild, often leading to a patching mandate. Organizations should aim to fix detected CVEs as early in this lifecycle as possible to avoid successful exploitation and limit business impact."));
const TickLabel = ({ label, tooltipText, }) => {
    return (React.createElement(React.Fragment, null,
        React.createElement("span", null, label),
        React.createElement(TooltipIcon, { size: "xs", title: tooltipText })));
};
const TotalCount = ({ vulnerabilityStatistics, }) => {
    const triagedCount = vulnerabilityStatistics[SDLCMetadata.vulnerability_metrics_t.vuln_triaged_k];
    const activeCount = vulnerabilityStatistics[SDLCMetadata.vulnerability_metrics_t.vuln_total_k] -
        triagedCount;
    return (React.createElement(React.Fragment, null,
        activeCount.toLocaleString(),
        " active + ",
        triagedCount.toLocaleString(),
        " ",
        "triaged"));
};
const Header = ({ vulnerabilityStatistics, totalCountWrapper, }) => {
    return (React.createElement("div", { className: styles["detail-row"] },
        React.createElement(RenderChildrenWrapper, { wrapper: totalCountWrapper && ((children) => totalCountWrapper(children)) },
            React.createElement(TotalCount, { vulnerabilityStatistics: vulnerabilityStatistics })),
        React.createElement(TooltipIcon, { size: "xs", title: LIFECYCLE_TOOLTIP })));
};
const Chart = ({ vulnerabilities, vulnerabilityStatistics, vulnerabilityWrapper, moreWrapper, totalCountWrapper, }) => {
    const stages = [
        {
            tag: "UNPROVEN",
            vulnerability: vulnerabilities.unproven,
            totalCount: vulnerabilities.unproven.total_count,
            label: TRANSFORM_EXPLOIT_FLAG.UNPROVEN,
            tooltipText: "Software composition analysis has identified one or more known vulnerabilities.",
        },
        {
            tag: "EXISTS",
            vulnerability: vulnerabilities.exploit_exists,
            totalCount: vulnerabilityStatistics[SDLCMetadata.vulnerability_metrics_t.vuln_exploit_k],
            label: TRANSFORM_EXPLOIT_FLAG.EXISTS,
            tooltipText: `Available threat intelligence telemetry has confirmed that
              the reported vulnerabilities are actively being exploited by malicious actors.`,
        },
        {
            tag: "MALWARE",
            vulnerability: vulnerabilities.exploited_by_malware,
            totalCount: vulnerabilityStatistics[SDLCMetadata.vulnerability_metrics_t.vuln_malware_k],
            label: TRANSFORM_EXPLOIT_FLAG.MALWARE,
            tooltipText: `Available threat intelligence telemetry has confirmed that
              the reported vulnerabilities are actively being exploited by malicious actors.
              Malware code that propagates through these vulnerabilities has been created.`,
        },
        {
            tag: "MANDATE",
            vulnerability: vulnerabilities.patch_mandated,
            totalCount: vulnerabilityStatistics[SDLCMetadata.vulnerability_metrics_t.vuln_mandate_k],
            label: TRANSFORM_EXPLOIT_FLAG.MANDATE,
            tooltipText: "Vulnerabilities are in the CISA Known Exploited Vulnerability (KEV) catalog.",
        },
    ];
    return (React.createElement("div", { className: styles.chart },
        totalCountWrapper && (React.createElement("div", { className: styles.summary },
            React.createElement(RenderChildrenWrapper, { wrapper: (children) => totalCountWrapper(children) },
                React.createElement(TotalCount, { vulnerabilityStatistics: vulnerabilityStatistics })),
            React.createElement(TooltipIcon, { size: "xs", title: LIFECYCLE_TOOLTIP }))),
        React.createElement("div", { className: styles.stages }, stages.map((stage) => (React.createElement("div", { key: stage.tag, className: styles.stage },
            stage.totalCount > 0 ? (React.createElement("div", { className: styles.card },
                React.createElement("div", { className: styles["card-header"] },
                    React.createElement("span", { className: styles.count }, stage.totalCount.toLocaleString()),
                    React.createElement("span", { className: styles.fixable },
                        "\u00B7 ",
                        stage.vulnerability.fixable_count.toLocaleString(),
                        " ",
                        "fixable")),
                React.createElement("div", { className: styles["card-body"] },
                    stage.vulnerability.top_3.map((item) => {
                        const severity = getCvssSeverity(item.cvss.base_score);
                        return (React.createElement("div", { key: item.cve, className: styles["cve-row"] },
                            React.createElement(RenderChildrenWrapper, { wrapper: vulnerabilityWrapper &&
                                    ((children) => vulnerabilityWrapper(item, children)) },
                                React.createElement(LongTextLabel, { label: item.cve })),
                            React.createElement("span", { className: styles[`chip-${severity}`] }, severity.charAt(0).toUpperCase())));
                    }),
                    stage.totalCount > 3 && (React.createElement(RenderChildrenWrapper, { wrapper: moreWrapper &&
                            ((children) => moreWrapper(stage.tag, children)) },
                        "+",
                        (stage.totalCount - 3).toLocaleString(),
                        " more"))))) : (React.createElement("div", { className: styles.empty }, "None")),
            React.createElement("span", { className: stage.totalCount > 0
                    ? styles["stem-populated"]
                    : styles["stem-empty"] }))))),
        React.createElement("div", { className: styles.track },
            React.createElement("span", { className: styles["start-dot"] }),
            React.createElement("span", { className: styles.line }),
            React.createElement("svg", { className: styles.arrow, width: "14", height: "10", viewBox: "0 0 14 10", "aria-hidden": "true" },
                React.createElement("path", { d: "M1 5 H9 M6 1.5 L10.5 5 L6 8.5", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" })),
            React.createElement("div", { className: styles.markers }, stages.map((stage) => (React.createElement("div", { key: stage.tag, className: styles["marker-cell"] },
                React.createElement("span", { className: stage.totalCount > 0
                        ? styles["marker-populated"]
                        : styles["marker-empty"] })))))),
        React.createElement("div", { className: styles.labels }, stages.map((stage) => (React.createElement("span", { key: stage.tag, className: stage.totalCount > 0 ? styles["label-active"] : styles.label },
            React.createElement(TickLabel, { label: stage.label, tooltipText: stage.tooltipText })))))));
};
const VulnerabilitiesPanel = ({ details, actions, children, }) => {
    return (React.createElement(Card.Card, null,
        React.createElement(Card.Title, { details: details, actions: actions }, "Vulnerabilities"),
        React.createElement(Card.Content, null, children)));
};
export default {
    Panel: VulnerabilitiesPanel,
    Header,
    Chart,
    Tooltip: VulnerabilityTooltip,
};
//# sourceMappingURL=Vulnerabilities.js.map